import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import { api } from "../../../lib/api";
import { cn } from "../../../lib/utils";
import {
  normalizeQuoteSnapshot,
  type QuoteMarketMark,
  type QuoteMarketPolicy,
  type QuoteSnapshot,
} from "./model";

type PreviewIssue = {
  month: string;
  areaCode: "HU" | "RO";
  code: string;
  message: string;
};

type PreviewRow = {
  month: string;
  currentHuPrice: number | null;
  proposedHuPrice: number | null;
  currentQuotedSpread: number | null;
  observedBasis: number | null;
  proposedQuotedSpread: number | null;
  hu: QuoteMarketMark | null;
  ro: QuoteMarketMark | null;
  warnings: PreviewIssue[];
};

type MarketPreview = {
  canApply: boolean;
  selectedPolicy: QuoteMarketPolicy;
  basisApplied: boolean;
  source: string;
  asOf: string | null;
  expiresAt: string | null;
  previewToken: string | null;
  rows: PreviewRow[];
  issues: PreviewIssue[];
};

export function QuoteMarketSnapshotDialog({
  snapshot,
  workspace,
  months,
  onClose,
  onApplied,
}: {
  snapshot: QuoteSnapshot;
  workspace: { id: string; version: number } | null;
  months: string[];
  onClose(): void;
  onApplied(snapshot: QuoteSnapshot): void;
}) {
  const [policy, setPolicy] = useState<QuoteMarketPolicy>("mid");
  const [applyBasis, setApplyBasis] = useState(false);
  const [preview, setPreview] = useState<MarketPreview | null>(null);

  const previewMutation = useMutation({
    mutationFn: () => api<{ preview: MarketPreview }>("/eex/quotes/market-snapshot/preview", {
      method: "POST",
      body: JSON.stringify({
        snapshot,
        workspace,
        months,
        selectedPolicy: policy,
        applyObservedBasis: applyBasis,
      }),
    }),
    onMutate: () => {
      setPreview(null);
    },
    onSuccess: (result) => setPreview(result.preview),
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!preview?.previewToken) throw new Error("Preview the complete selection first.");
      const result = await api<{ snapshot: unknown }>("/eex/quotes/market-snapshot/apply", {
        method: "POST",
        body: JSON.stringify({ snapshot, workspace, previewToken: preview.previewToken }),
      });
      const normalized = normalizeQuoteSnapshot(result.snapshot);
      if (!normalized || normalized.schemaVersion !== 2) {
        throw new Error("Cascena returned an invalid market snapshot.");
      }
      return normalized;
    },
    onSuccess: onApplied,
  });

  const changePolicy = (next: QuoteMarketPolicy) => {
    setPolicy(next);
    setPreview(null);
    previewMutation.reset();
    applyMutation.reset();
  };
  const changeBasis = (next: boolean) => {
    setApplyBasis(next);
    setPreview(null);
    previewMutation.reset();
    applyMutation.reset();
  };
  const busy = previewMutation.isPending || applyMutation.isPending;
  const error = previewMutation.error ?? applyMutation.error;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-4" role="dialog" aria-modal="true" aria-labelledby="quote-market-title">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-border bg-bg shadow-2xl">
        <div className="flex items-start gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 id="quote-market-title" className="text-sm font-semibold">Apply EEX market snapshot</h2>
            <p className="mt-0.5 text-[11px] text-muted">
              {months.length} selected month{months.length === 1 ? "" : "s"} · exact-month simple HU baseload futures, with optional matching RO basis
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="ml-auto rounded p-1 text-muted hover:bg-border/40 hover:text-fg" aria-label="Close market snapshot dialog"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex flex-wrap items-end gap-4 border-b border-border px-4 py-3">
          <label className="space-y-1 text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
            <span>One mark policy for all months</span>
            <select value={policy} disabled={busy} onChange={(event) => changePolicy(event.target.value as QuoteMarketPolicy)} className="block h-8 min-w-36 rounded border border-border bg-bg px-2 text-xs normal-case tracking-normal text-fg">
              <option value="bid">Bid</option>
              <option value="ask">Ask</option>
              <option value="mid">Mid</option>
              <option value="last">Last trade</option>
            </select>
          </label>
          <label className="flex h-8 items-center gap-2 rounded border border-border px-3 text-xs">
            <input type="checkbox" checked={applyBasis} disabled={busy} onChange={(event) => changeBasis(event.target.checked)} />
            Apply observed RO − HU basis
          </label>
          <span className="text-[10px] text-muted">Off by default. Risk and FX are never changed.</span>
          <button type="button" disabled={busy || months.length === 0} onClick={() => previewMutation.mutate()} className="ml-auto h-8 rounded border border-accent px-3 text-xs font-medium text-accent hover:bg-accent/10 disabled:opacity-40">
            {previewMutation.isPending ? "Building preview…" : preview ? "Refresh preview" : "Preview changes"}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {!preview && !error && (
            <div className="p-8 text-center text-xs text-muted">Preview reads Cascena’s cached EEX data. It never calls EEX from the browser.</div>
          )}
          {error && (
            <div className="m-4 flex items-start gap-2 rounded border border-negRed/30 bg-negRed/5 p-3 text-xs text-negRed"><TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />{errorMessage(error)}</div>
          )}
          {preview && (
            <>
              <div className={cn("m-4 flex items-start gap-2 rounded border p-3 text-xs", preview.canApply ? "border-posGreen/30 bg-posGreen/5 text-posGreen" : "border-negRed/30 bg-negRed/5 text-negRed")}>
                {preview.canApply ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />}
                <span>{preview.canApply ? `Complete atomic preview · ${preview.source} · as of ${preview.asOf}` : "Nothing will be applied until every required HU value, and every requested RO basis value, passes validation."}</span>
              </div>
              {preview.issues.length > 0 && (
                <ul className="mx-4 mb-4 space-y-1 text-xs text-negRed">
                  {preview.issues.map((issue, index) => <li key={`${issue.month}-${issue.areaCode}-${issue.code}-${index}`}>{issue.month} · {issue.areaCode}: {issue.message}</li>)}
                </ul>
              )}
              <table className="min-w-[1180px] w-full border-collapse text-xs">
                <thead className="sticky top-0 bg-bg text-left text-[10px] uppercase tracking-[0.06em] text-muted">
                  <tr>
                    <th className="border-y border-border px-4 py-2">Month</th>
                    <th className="border-y border-border px-3 py-2">HU current</th>
                    <th className="border-y border-border px-3 py-2">HU market snapshot</th>
                    <th className="border-y border-border px-3 py-2">RO market snapshot</th>
                    <th className="border-y border-border px-3 py-2">Observed basis</th>
                    <th className="border-y border-border px-3 py-2">Quoted basis</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.month} className="border-b border-border/60">
                      <td className="px-4 py-2 font-mono font-medium">{row.month}</td>
                      <td className="px-3 py-2 font-mono">{number(row.currentHuPrice)}</td>
                      <MarketSnapshotCell mark={row.hu} policy={policy} proposed={row.proposedHuPrice} />
                      <MarketSnapshotCell mark={row.ro} policy={policy} proposed={row.ro?.chosen ?? null} />
                      <td className="px-3 py-2 font-mono">{number(row.observedBasis)}</td>
                      <td className="px-3 py-2 font-mono">
                        <div className="font-semibold">After {number(row.proposedQuotedSpread)}</div>
                        <div className="text-[10px] text-muted">Current {number(row.currentQuotedSpread)}</div>
                        {row.warnings.length > 0 && <div className="font-sans text-[10px] text-warnOrange">RO unavailable; basis left manual</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border px-4 py-3">
          <span className="text-[10px] text-muted">Apply changes the entire preview or nothing, resets input confirmation, and freezes source provenance.</span>
          <button type="button" onClick={onClose} disabled={busy} className="ml-auto h-8 rounded border border-border px-3 text-xs">Cancel</button>
          <button type="button" disabled={!preview?.canApply || !preview.previewToken || busy} onClick={() => applyMutation.mutate()} className="h-8 rounded bg-accent px-3 text-xs font-medium text-white disabled:opacity-40">
            {applyMutation.isPending ? "Applying…" : `Apply ${months.length} month${months.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarketSnapshotCell({
  mark,
  policy,
  proposed,
}: {
  mark: QuoteMarketMark | null;
  policy: QuoteMarketPolicy;
  proposed: number | null;
}) {
  if (!mark) return <td className="px-3 py-2 text-muted">Unavailable</td>;
  const priceScopeId = mark.priceSourceScopeId ?? mark.acquisitionScopeId;
  const selectedSource = policy === "last" ? "EEX public trades" : "EEX top of book";
  const provenance = [
    `top-of-book scope ${mark.acquisitionScopeId}`,
    `chosen-price scope ${priceScopeId}`,
    `instrument ${mark.marketInstrumentId}`,
    `provider key ${mark.providerInstrumentKey}`,
    mark.instrumentIsin ? `ISIN ${mark.instrumentIsin}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <td className="min-w-72 px-3 py-2 align-top" title={provenance}>
      <div className="font-mono font-semibold">Chosen {policy}: {number(proposed)}</div>
      <div className="mt-1 whitespace-nowrap font-mono text-[10px]">
        Bid {number(mark.bid)} · Ask {number(mark.ask)} · Mid {number(mark.mid)} · Last {number(mark.last)}
      </div>
      <div className="whitespace-nowrap font-mono text-[10px] text-muted">
        Volume B {number(mark.bidVolume)} · A {number(mark.askVolume)} · Last {number(mark.lastVolume)}
      </div>
      <div className="mt-1 text-[10px] text-muted">
        Source: EEX DataSource · {selectedSource}
      </div>
      <div className="text-[10px] text-muted" title={mark.marketTimestamp}>
        Market {timestamp(mark.marketTimestamp)}
      </div>
      <div className="text-[10px] text-muted" title={mark.receivedAt}>
        Received {timestamp(mark.receivedAt)} · {age(mark.receivedAt)} ago
      </div>
    </td>
  );
}

function number(value: number | null): string {
  return value == null ? "—" : value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function age(receivedAt: string): string {
  const milliseconds = Date.now() - Date.parse(receivedAt);
  if (!Number.isFinite(milliseconds)) return "—";
  if (milliseconds < 60_000) return `${Math.max(0, Math.round(milliseconds / 1_000))}s`;
  return `${Math.round(milliseconds / 60_000)}m`;
}

function timestamp(value: string): string {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) ? new Date(milliseconds).toLocaleString() : "—";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Could not load the EEX market preview.";
}
